import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  CustomerSelect,
  CompanySelect,
  FormatDateToMDY,
  FormatValueToDecimal,
  DateSelect
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import { PDFDocument, rgb, StandardFonts  } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function CustomerLedger() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Customer 
  const [customer, setCustomer] = useState(null);
  const [company, setCompany] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);

  const openCustomerSelect = () => {
    setShowCustomerSelect(true)
  }
  const closeCustomerSelect = () => {
    setShowCustomerSelect(false)
  }
//#endregion








//#region Generate
  const fetchReportData = useCallback(async (_data) => {
    const body = {
      customerId: _data.customerId,
      dateFrom: dateFrom,
      dateTo: dateTo,
    }

    try {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/customer-ledger`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [dateFrom, dateTo]);

  const generateHandleClick = async () => {
    if (!customer || !company || !dateFrom || !dateTo) {
      setFailMessage("Please fill up required fields");
      return;
    }

    setLoading(true);
    const _data = await fetchReportData(customer);

    try {
      const pdfUrl = `${window.location.origin}/docs/Customer_Ledger.pdf`;
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontBytes = await (await fetch("/calibri.ttf")).arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);
      const boldBytes = await (await fetch("/calibrib.ttf")).arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

      const wrapText = (text, maxWidth, font, fontSize) => {
        if (!text) return [""];
        const words = text.split(/(?<=-)|\s+/);
        const lines = [];
        let currentLine = '';
        for (const word of words) {
          const testLine = currentLine ? `${currentLine}${word}` : word;
          if (font.widthOfTextAtSize(testLine, fontSize) <= maxWidth) {
            currentLine = testLine;
          } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
          }
        }
        if (currentLine) lines.push(currentLine);
        return lines;
      };

      // --- REUSABLE HEADER FUNCTION ---
      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 580, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 570, size: 7, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 560, size: 7, font: calibriFont });
        targetPage.drawText(customer?.name1?.toString() ?? "", { x: 35, y: 540, size: 7, font: calibriFont });
        targetPage.drawText(`${FormatDateToMDY(dateFrom)} - ${FormatDateToMDY(dateTo)}`, { x: 93, y: 515.5, size: 7, font: calibriFont });
        targetPage.drawText(FormatDateToMDY(today), { x: 82, y: 505.5, size: 7, font: calibriFont });
        
        // Draw Table Column Headers if your template doesn't have them on every page
        // targetPage.drawText("Date", { x: 37.5, y: 460, size: 7, font: calibriBold }); 
      };

      let currentPage = pdfDoc.getPages()[0];
      drawHeader(currentPage);

      let yPosition = 440;
      const bottomThreshold = 60; // When to trigger a new page
      const colWidthDebitDataDocNoLines = 25;
      const colWidthCreditDataDocNoLines = 50;
      const colWidthCreditDataCheckbookNumberLines = 50;
      const lineHeight = 10;

      let begBalance = 0;
      let sales = 0;
      let collection = 0;
      let upappliedCollection = 0;
      let endingBalance = 0;

      for (let index = 0; index < _data.length; index++) {
        // --- NEW PAGE CHECK ---
        if (yPosition < bottomThreshold) {
          currentPage = pdfDoc.addPage([currentPage.getWidth(), currentPage.getHeight()]);
          drawHeader(currentPage);
          yPosition = 480; 
        }

        const rowStartY = yPosition;

        // --- DEBIT COLUMN ---
        let debitYCursor = rowStartY;
        const creditDateObj = _data[index]?.details?.credit?.date ? new Date(_data[index].details.credit.date) : null;

        for (let detail = 0; detail < _data[index]?.details?.debit?.length; detail++) {
          const debitData = _data[index]?.details?.debit?.[detail];

          currentPage.drawText(`${FormatDateToMDY(debitData.date)}`, { x: 37.5, y: debitYCursor, size: 7, font: calibriFont });
          currentPage.drawText(`SAL`, { x: 85, y: debitYCursor, size: 7, font: calibriFont });

          const debitLines = wrapText(debitData.documentNumber?.toString() ?? "", colWidthDebitDataDocNoLines, calibriFont, 7);
          
          // FIX 1: Replaced .forEach with a standard for loop
          for (let j = 0; j < debitLines.length; j++) {
            currentPage.drawText(debitLines[j], { 
              x: 118, 
              y: debitYCursor - (j * lineHeight), 
              size: 7, 
              font: calibriFont 
            });
          }

          currentPage.drawText(debitData.referenceNumber?.toString() ?? "-", { x: 170, y: debitYCursor, size: 7, font: calibriFont });
          currentPage.drawText(FormatValueToDecimal(debitData.amount)?.toString() ?? "", { x: 218, y: debitYCursor, size: 7, font: calibriFont });
          currentPage.drawText(FormatValueToDecimal(debitData.balance)?.toString() ?? "", { x: 278, y: debitYCursor, size: 7, font: calibriFont });

          let daysDiff = "-";
          if (debitData.date && creditDateObj) {
            const debitDateObj = new Date(debitData.date);
            const timeDiff = creditDateObj.getTime() - debitDateObj.getTime();
            daysDiff = Math.max(1, Math.ceil(timeDiff / (1000 * 3600 * 24))).toString();
          }
          currentPage.drawText(daysDiff, { x: 465, y: debitYCursor, size: 7, font: calibriFont });

          const itemHeight = Math.max(15, (debitLines.length * lineHeight) + 2);
          debitYCursor -= itemHeight;
          
          if (debitYCursor < bottomThreshold) {
              currentPage = pdfDoc.addPage([currentPage.getWidth(), currentPage.getHeight()]);
              drawHeader(currentPage);
              debitYCursor = 480; 
          }

          begBalance += debitData.balance
          sales += debitData.amount
        }

        // --- CREDIT COLUMN ---
        let creditYCursor = rowStartY;
        const creditData = _data[index]?.details?.credit;

        if (creditData) {
          currentPage.drawText(`${FormatDateToMDY(creditData.date)}`, { x: 336, y: creditYCursor, size: 7, font: calibriFont });
          currentPage.drawText(`ARP`, { x: 380, y: creditYCursor, size: 7, font: calibriFont });

          const creditLines = wrapText(creditData.documentNumber?.toString() ?? "", colWidthCreditDataDocNoLines, calibriFont, 7);
          
          // FIX 2: Replaced .forEach with a standard for loop
          for (let j = 0; j < creditLines.length; j++) {
            currentPage.drawText(creditLines[j], { 
              x: 411, 
              y: creditYCursor - (j * lineHeight), 
              size: 7, 
              font: calibriFont 
            });
          }

          currentPage.drawText(creditData.bankName?.toString() ?? "-", { x: 557, y: creditYCursor, size: 7, font: calibriFont });

          const checkLines = wrapText(creditData.checkbookNumber?.toString() ?? "", colWidthCreditDataCheckbookNumberLines, calibriFont, 7);
          
          // FIX 3: Replaced .forEach with a standard for loop
          for (let j = 0; j < checkLines.length; j++) {
            currentPage.drawText(checkLines[j], { 
              x: 593, 
              y: creditYCursor - (j * lineHeight), 
              size: 7, 
              font: calibriFont 
            });
          }

          currentPage.drawText(`${FormatDateToMDY(creditData.checkbookDate)}`, { x: 653, y: creditYCursor, size: 7, font: calibriFont });
          currentPage.drawText(FormatValueToDecimal(creditData.amount) ?? "", { x: 706, y: creditYCursor, size: 7, font: calibriFont });

          const creditMaxLines = Math.max(creditLines.length, checkLines.length);
          creditYCursor -= Math.max(15, (creditMaxLines * lineHeight) + 2);

          collection += creditData.amount
        }

        yPosition = Math.min(debitYCursor, creditYCursor);
        currentPage.drawText("______________________________________________________________________________________________________________________________________________________________________________________________________________", {
          x: 35, y: yPosition + 5, size: 7, font: calibriFont,
        });
        yPosition -= 10;
      }

      // Beg Bal and Footer Info
      if (yPosition < 160) { 
        currentPage = pdfDoc.addPage([currentPage.getWidth(), currentPage.getHeight()]);
        yPosition = 550; // Reset to top of new page
        drawHeader(currentPage); // Draw header on the new page too
      }
      upappliedCollection = sales - collection
      endingBalance = sales - collection

      currentPage.drawText("TOTAL", { x: 35, y: yPosition, size: 7, font: calibriBold });
      currentPage.drawText(FormatValueToDecimal(begBalance)?.toString() ?? "", { x: 218, y: yPosition, size: 7, font: calibriBold });
      currentPage.drawText(FormatValueToDecimal(sales)?.toString() ?? "", { x: 278, y: yPosition, size: 7, font: calibriBold });
      currentPage.drawText(FormatValueToDecimal(collection)?.toString() ?? "", { x: 706, y: yPosition, size: 7, font: calibriBold });

      currentPage.drawText("NET AMOUNT", { x: 336, y: yPosition - 10, size: 7, font: calibriBold });
      currentPage.drawText(FormatValueToDecimal(upappliedCollection)?.toString() ?? "", { x: 706, y: yPosition - 10, size: 7, font: calibriBold });

      let footerY = yPosition - 40;
    
      currentPage.drawText("*** Customer is member of Payment Group", { x: 35, y: footerY, size: 7, font: helveticaFont });

      currentPage.drawText("Beg. Bal:", { x: 35, y: footerY - 20, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(begBalance)?.toString() ?? "", { x: 170, y: footerY - 20, size: 7, font: helveticaFont });
      
      currentPage.drawText("Sales:", { x: 35, y: footerY - 30, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(sales)?.toString() ?? "", { x: 170, y: footerY - 30, size: 7, font: helveticaFont });

      currentPage.drawText("Other Receivables:", { x: 35, y: footerY - 40, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 40, size: 7, font: helveticaFont });

      currentPage.drawText("Less:", { x: 35, y: footerY - 50, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 50, size: 7, font: helveticaFont });

      currentPage.drawText("Cash Sales:", { x: 35, y: footerY - 60, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 60, size: 7, font: helveticaFont });

      currentPage.drawText("Sales Return:", { x: 35, y: footerY - 70, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 70, size: 7, font: helveticaFont });

      currentPage.drawText("EWT:", { x: 35, y: footerY - 80, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 80, size: 7, font: helveticaFont });

      currentPage.drawText("EVAT:", { x: 35, y: footerY - 90, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 90, size: 7, font: helveticaFont });

      currentPage.drawText("Collection:", { x: 35, y: footerY - 100, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(collection)?.toString() ?? "", { x: 170, y: footerY - 100, size: 7, font: helveticaFont });

      currentPage.drawText("Unapplied Collection:", { x: 35, y: footerY - 110, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(upappliedCollection)?.toString() ?? "", { x: 170, y: footerY - 110, size: 7, font: helveticaFont });

      currentPage.drawText("Ending Bal.:", { x: 35, y: footerY - 120, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(endingBalance)?.toString(), { x: 170, y: footerY - 120, size: 7, font: helveticaFont });

      currentPage.drawText("Direct Collection:", { x: 35, y: footerY - 130, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 130, size: 7, font: helveticaFont });

      // --- ADD PAGE NUMBERS ---
      const pages = pdfDoc.getPages();
      for (let i = 0; i < pages.length; i++) {
        pages[i].drawText(`Page ${i + 1} of ${pages.length}`, {
          x: pages[i].getWidth() - 100,
          y: 20,
          size: 8,
          font: calibriFont,
          color: rgb(0, 0, 0),
        });
      }

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Customer_Ledger.pdf`;
      a.click();
    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasCustomerLedger) {
    return <NoAccess />;
  }
//#endregion





//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`Customer Ledger`}
            </h2>
            <p className="text-gray-500 mt-1">Record of all transactions related to each customer.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              Customer <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Customer"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={`${customer ? `${customer?.customerId} - ${customer?.name1}` : ""}`}
                readOnly
              />
              {!customer ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openCustomerSelect}>
                  <PersonSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setCustomer(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date From <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateFrom} setData={setDateFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date To <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateTo} setData={setDateTo} isEditable={true}/>
          </div>
        </div>

        <button onClick={generateHandleClick} className="mt-10 flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
          <RestartAltIcon fontSize="small" /> Generate
        </button>
      </div>

      <CustomerSelect 
        visible={showCustomerSelect} 
        onClose={closeCustomerSelect}
        modalName={"Select Customer"}
        data={customer} setData={setCustomer}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}